import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ThemedButton } from '../ThemedButton';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import { useWizard } from '../../contexts/WizardContext';

const FraudWarranty = () => {
  const { formData, updateFormData, nextStep } = useWizard();
  const { control, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: {
      fraud_warranty_accepted: formData.fraud_warranty_accepted,
    }
  });

  const fraudWarrantyAccepted = watch('fraud_warranty_accepted');

  const onSubmit = (data) => {
    if (!data.fraud_warranty_accepted) {
      return;
    }
    updateFormData({
      ...data,
      fraud_warranty_timestamp: new Date().toISOString()
    });
    nextStep();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Please read the fraud warranty carefully before proceeding.
        </AlertDescription>
      </Alert>

      <div className="border border-slate-200 rounded-lg p-6 bg-slate-50 max-h-96 overflow-y-auto">
        <h3 className="text-lg font-bold text-slate-900 mb-4">Fraud Warranty Statement</h3>
        
        <div className="space-y-4 text-sm text-slate-700 leading-relaxed">
          <p className="font-semibold">
            I hereby warrant and represent that:
          </p>
          
          <ul className="list-disc pl-6 space-y-2">
            <li>
              All information provided in this application is true, accurate, and complete to the best of my knowledge and belief.
            </li>
            <li>
              I understand that any misrepresentation, concealment, or fraud in this application may result in the denial of coverage or cancellation of any policy issued.
            </li>
            <li>
              I have disclosed all material facts that may affect the underwriting decision, including but not limited to previous claims, losses, or incidents.
            </li>
            <li>
              I understand that the insurer relies on the accuracy of the information provided in determining whether to issue coverage and at what premium.
            </li>
            <li>
              Any person who knowingly and with intent to defraud any insurance company or other person files an application for insurance containing any materially false information or conceals information concerning any fact material thereto commits a fraudulent insurance act, which is a crime and may be subject to civil and criminal penalties.
            </li>
          </ul>

          <p className="font-semibold mt-6">
            By checking the box below, I affirm that I have read and understand this fraud warranty and that all information provided is truthful and complete.
          </p>
        </div>
      </div>

      <div>
        <Controller
          name="fraud_warranty_accepted"
          control={control}
          rules={{ required: 'You must accept the fraud warranty to proceed' }}
          render={({ field }) => (
            <div className="flex items-start space-x-3">
              <Checkbox
                id="fraud_warranty"
                data-testid="fraud-warranty-checkbox"
                checked={field.value}
                onCheckedChange={field.onChange}
                className="mt-1"
              />
              <Label htmlFor="fraud_warranty" className="font-normal cursor-pointer leading-relaxed">
                I have read and accept the fraud warranty statement above. I certify that all information provided in this application is true and accurate. *
              </Label>
            </div>
          )}
        />
        {errors.fraud_warranty_accepted && (
          <p className="text-sm text-red-600 mt-2">{errors.fraud_warranty_accepted.message}</p>
        )}
      </div>

      <ThemedButton 
        type="submit"
        data-testid="fraud-warranty-next-btn"
        disabled={!fraudWarrantyAccepted}
        variant="wizard" size="full"
      >
        Next: Additional Insured
      </ThemedButton>
    </form>
  );
};

export default FraudWarranty;